import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import 'react-datepicker/dist/react-datepicker.css';
import './DateRangePicker.css';
import { createPortal } from 'react-dom';
import useThemeColor from '../../../hooks/useThemeColor';

const PANEL_MIN_WIDTH = 480;
const PANEL_MAX_WIDTH = 520;
const PANEL_GAP = 4;
const VIEWPORT_PAD = 8;

const DateRangePicker = ({
  startDate,
  endDate,
  onChange,
  placeholder = 'Select Date Range',
  className = '',
  disabled = false,
  allowSingleDate = true,
  allowDateRange = true,
  panelMaxWidth = PANEL_MAX_WIDTH,
  id,
  /** When true, always show formatted dates (e.g. "Oct 09 – Oct 16, 2026") instead of preset labels */
  forceDateLabel = false,
}) => {
  const themeColor = useThemeColor();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [selectionMode, setSelectionMode] = useState(
    allowDateRange ? 'range' : 'single'
  );
  const [tempStartDate, setTempStartDate] = useState(null);
  const [tempEndDate, setTempEndDate] = useState(null);
  const dropdownRef = useRef(null);
  const containerRef = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState(null);
  const [hasMounted, setHasMounted] = useState(false);

  useEffect(() => {
    setHasMounted(true);
  }, []);

  const dateRangePresets = useMemo(
    () => [
      {
        label: 'Today',
        value: 'today',
        getDates: () => [
          moment().startOf('day').toDate(),
          moment().endOf('day').toDate(),
        ],
      },
      {
        label: 'Yesterday',
        value: 'yesterday',
        getDates: () => [
          moment().subtract(1, 'day').startOf('day').toDate(),
          moment().subtract(1, 'day').endOf('day').toDate(),
        ],
      },
      {
        label: 'This Week',
        value: 'thisWeek',
        getDates: () => [
          moment().startOf('isoWeek').toDate(),
          moment().endOf('isoWeek').toDate(),
        ],
      },
      {
        label: 'Last Week',
        value: 'lastWeek',
        getDates: () => [
          moment().subtract(1, 'week').startOf('isoWeek').toDate(),
          moment().subtract(1, 'week').endOf('isoWeek').toDate(),
        ],
      },
      {
        label: 'This Month',
        value: 'thisMonth',
        getDates: () => [
          moment().startOf('month').toDate(),
          moment().endOf('month').toDate(),
        ],
      },
      {
        label: 'Last Month',
        value: 'lastMonth',
        getDates: () => [
          moment().subtract(1, 'month').startOf('month').toDate(),
          moment().subtract(1, 'month').endOf('month').toDate(),
        ],
      },
      {
        label: 'Last 7 Days',
        value: 'last7Days',
        getDates: () => [
          moment().subtract(6, 'days').startOf('day').toDate(),
          moment().endOf('day').toDate(),
        ],
      },
      {
        label: 'Last 30 Days',
        value: 'last30Days',
        getDates: () => [
          moment().subtract(29, 'days').startOf('day').toDate(),
          moment().endOf('day').toDate(),
        ],
      },
      {
        label: 'Last 90 Days',
        value: 'last90Days',
        getDates: () => [
          moment().subtract(89, 'days').startOf('day').toDate(),
          moment().endOf('day').toDate(),
        ],
      },
    ],
    []
  );

  const singleDatePresets = useMemo(
    () => [
      {
        label: 'Today',
        value: 'today',
        getDate: () => moment().startOf('day').toDate(),
      },
      {
        label: 'Yesterday',
        value: 'yesterday',
        getDate: () => moment().subtract(1, 'day').startOf('day').toDate(),
      },
      {
        label: 'This Monday',
        value: 'thisMonday',
        getDate: () => moment().startOf('isoWeek').toDate(),
      },
      {
        label: 'Last Monday',
        value: 'lastMonday',
        getDate: () => moment().subtract(1, 'week').startOf('isoWeek').toDate(),
      },
      {
        label: 'First of Month',
        value: 'firstOfMonth',
        getDate: () => moment().startOf('month').toDate(),
      },
      {
        label: 'Last of Month',
        value: 'lastOfMonth',
        getDate: () => moment().endOf('month').toDate(),
      },
    ],
    []
  );

  const isValidDate = (date) =>
    date && date instanceof Date && !isNaN(date.getTime());

  const updateDropdownPosition = useCallback(() => {
    if (!containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const isNarrow = viewportWidth < 640;

    if (isNarrow) {
      const width = Math.min(viewportWidth - VIEWPORT_PAD * 2, 420);
      setDropdownPosition({
        top: Math.max(VIEWPORT_PAD, Math.min(rect.bottom + PANEL_GAP, window.innerHeight * 0.08)),
        left: (viewportWidth - width) / 2,
        width,
        maxHeight: window.innerHeight - VIEWPORT_PAD * 2,
        mobile: true,
      });
      return;
    }

    const preferredMax = Math.max(PANEL_MIN_WIDTH, Number(panelMaxWidth) || PANEL_MAX_WIDTH);
    const width = Math.min(
      preferredMax,
      Math.max(PANEL_MIN_WIDTH, rect.width),
      viewportWidth - VIEWPORT_PAD * 2
    );
    let left = rect.left;
    if (left + width > viewportWidth - VIEWPORT_PAD) {
      left = Math.max(VIEWPORT_PAD, rect.right - width);
    }
    if (left < VIEWPORT_PAD) {
      left = VIEWPORT_PAD;
    }

    const estimatedHeight = 420;
    let top = rect.bottom + PANEL_GAP;
    if (top + estimatedHeight > window.innerHeight - VIEWPORT_PAD) {
      top = Math.max(VIEWPORT_PAD, rect.top - estimatedHeight - PANEL_GAP);
    }

    setDropdownPosition({
      top,
      left,
      width,
      maxHeight: window.innerHeight - VIEWPORT_PAD * 2,
      mobile: false,
    });
  }, [panelMaxWidth]);

  useEffect(() => {
    if (!isOpen) {
      setDropdownPosition(null);
      return;
    }
    updateDropdownPosition();
    const handleScrollOrResize = () => updateDropdownPosition();
    window.addEventListener('scroll', handleScrollOrResize, true);
    window.addEventListener('resize', handleScrollOrResize);
    return () => {
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, [isOpen, updateDropdownPosition]);

  useEffect(() => {
    if (!isOpen) return;
    setTempStartDate(isValidDate(startDate) ? startDate : null);
    setTempEndDate(isValidDate(endDate) ? endDate : null);
    if (isValidDate(startDate) && !isValidDate(endDate) && allowSingleDate) {
      setSelectionMode('single');
    } else if (isValidDate(startDate) && isValidDate(endDate) && allowDateRange) {
      setSelectionMode('range');
    }
  }, [isOpen, startDate, endDate, allowSingleDate, allowDateRange]);

  useEffect(() => {
    if (isValidDate(startDate) && isValidDate(endDate)) {
      const currentStart = moment(startDate);
      const currentEnd = moment(endDate);
      const matchedPreset = dateRangePresets.find((preset) => {
        const [presetStart, presetEnd] = preset.getDates();
        return (
          currentStart.isSame(moment(presetStart), 'day') &&
          currentEnd.isSame(moment(presetEnd), 'day')
        );
      });
      setSelectedPreset(matchedPreset ? matchedPreset.value : null);
    } else if (isValidDate(startDate) && !isValidDate(endDate)) {
      const currentDate = moment(startDate);
      const matchedPreset = singleDatePresets.find((preset) =>
        currentDate.isSame(moment(preset.getDate()), 'day')
      );
      setSelectedPreset(matchedPreset ? matchedPreset.value : null);
    } else {
      setSelectedPreset(null);
    }
  }, [startDate, endDate, dateRangePresets, singleDatePresets]);

  const closeDropdown = useCallback(() => setIsOpen(false), []);

  const handleRangePresetSelect = (preset) => {
    const [newStartDate, newEndDate] = preset.getDates();
    setSelectedPreset(preset.value);
    setSelectionMode('range');
    setTempStartDate(newStartDate);
    setTempEndDate(newEndDate);
    onChange([newStartDate, newEndDate]);
    closeDropdown();
  };

  const handleSinglePresetSelect = (preset) => {
    const newDate = preset.getDate();
    setSelectedPreset(preset.value);
    setSelectionMode('single');
    setTempStartDate(newDate);
    setTempEndDate(null);
    onChange([newDate, null]);
    closeDropdown();
  };

  const handleDateChange = (dates) => {
    try {
      if (!dates) return;

      if (selectionMode === 'single') {
        if (isValidDate(dates)) {
          setTempStartDate(dates);
          setTempEndDate(null);
          setSelectedPreset(null);
        }
        return;
      }

      if (Array.isArray(dates)) {
        const [newStartDate, newEndDate] = dates;
        setTempStartDate(isValidDate(newStartDate) ? newStartDate : null);
        setTempEndDate(isValidDate(newEndDate) ? newEndDate : null);
        setSelectedPreset(null);
      } else if (isValidDate(dates)) {
        setTempStartDate(dates);
        setTempEndDate(null);
        setSelectedPreset(null);
      }
    } catch (_) {
      // Ignore malformed picker payloads.
    }
  };

  const canApply =
    isValidDate(tempStartDate) &&
    (selectionMode === 'single' || isValidDate(tempEndDate));

  const handleApplyCustomSelection = () => {
    if (selectionMode === 'single' && isValidDate(tempStartDate)) {
      setSelectedPreset(null);
      onChange([tempStartDate, null]);
      closeDropdown();
    } else if (
      selectionMode === 'range' &&
      isValidDate(tempStartDate) &&
      isValidDate(tempEndDate)
    ) {
      setSelectedPreset(null);
      onChange([tempStartDate, tempEndDate]);
      closeDropdown();
    }
  };

  const handleClearSelection = () => {
    setSelectedPreset(null);
    setTempStartDate(null);
    setTempEndDate(null);
    onChange([null, null]);
    closeDropdown();
  };

  const getDisplayText = () => {
    if (!forceDateLabel && selectedPreset) {
      const rangePreset = dateRangePresets.find((p) => p.value === selectedPreset);
      const singlePreset = singleDatePresets.find((p) => p.value === selectedPreset);
      return rangePreset?.label || singlePreset?.label || 'Custom Selection';
    }

    if (isValidDate(startDate) && isValidDate(endDate)) {
      const start = moment(startDate).format('MMM DD');
      const end = moment(endDate).format('MMM DD, YYYY');
      return `${start} – ${end}`;
    }
    if (isValidDate(startDate)) {
      return moment(startDate).format('MMM DD, YYYY');
    }
    return placeholder;
  };

  const getSelectionSummary = () => {
    if (selectionMode === 'single') {
      return isValidDate(tempStartDate)
        ? moment(tempStartDate).format('MMM D, YYYY')
        : 'Pick a date';
    }
    if (isValidDate(tempStartDate) && isValidDate(tempEndDate)) {
      return `${moment(tempStartDate).format('MMM D')} – ${moment(tempEndDate).format('MMM D, YYYY')}`;
    }
    if (isValidDate(tempStartDate)) {
      return `${moment(tempStartDate).format('MMM D, YYYY')} – …`;
    }
    return 'Pick a start and end date';
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!isOpen) return;
      const clickedInsideDropdown =
        dropdownRef.current && dropdownRef.current.contains(event.target);
      const clickedInput =
        containerRef.current && containerRef.current.contains(event.target);
      if (!clickedInsideDropdown && !clickedInput) {
        closeDropdown();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, closeDropdown]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeDropdown();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, closeDropdown]);

  const toggleOpen = () => {
    if (disabled) return;
    setIsOpen((open) => !open);
  };

  const handleTriggerKeyDown = (event) => {
    if (disabled) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleOpen();
    }
  };

  const handleModeChange = (mode) => {
    if (mode === 'single' && !allowSingleDate) return;
    if (mode === 'range' && !allowDateRange) return;
    setSelectionMode(mode);
    if (mode === 'single') {
      setTempEndDate(null);
    }
  };

  const themeStyle = {
    '--drp-accent': themeColor,
    '--drp-accent-soft': `${themeColor}22`,
    '--drp-accent-shadow': `${themeColor}40`,
  };

  const dropdownContent = (
    <div
      className={`date-range-dropdown${dropdownPosition?.mobile ? ' date-range-dropdown--mobile' : ''}`}
      ref={dropdownRef}
      role="dialog"
      aria-label="Select date or date range"
      style={
        hasMounted && dropdownPosition
          ? {
              position: 'fixed',
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              width: dropdownPosition.width,
              maxHeight: dropdownPosition.maxHeight,
              ...themeStyle,
            }
          : themeStyle
      }
    >
      <div className="date-range-unified">
        <div className="mode-toggle" role="tablist" aria-label="Selection mode">
          <button
            type="button"
            role="tab"
            aria-selected={selectionMode === 'single'}
            className={`mode-button ${selectionMode === 'single' ? 'active' : ''}`}
            onClick={() => handleModeChange('single')}
            disabled={!allowSingleDate}
          >
            <i className="fa fa-calendar-day" aria-hidden="true"></i>
            <span>Single Date</span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={selectionMode === 'range'}
            className={`mode-button ${selectionMode === 'range' ? 'active' : ''}`}
            onClick={() => handleModeChange('range')}
            disabled={!allowDateRange}
          >
            <i className="fa fa-calendar-week" aria-hidden="true"></i>
            <span>Date Range</span>
          </button>
        </div>

        <div className="date-range-unified-body">
          <div className="date-range-presets-col">
            <div className="presets-header">
              <i className="fa fa-bolt" aria-hidden="true"></i>
              <span>Quick select</span>
            </div>
            <div className="presets-list" role="listbox" aria-label="Date presets">
              {selectionMode === 'single'
                ? singleDatePresets.map((preset) => (
                    <button
                      key={preset.value}
                      type="button"
                      role="option"
                      aria-selected={selectedPreset === preset.value}
                      className={`preset-button ${selectedPreset === preset.value ? 'active' : ''}`}
                      onClick={() => handleSinglePresetSelect(preset)}
                    >
                      {preset.label}
                    </button>
                  ))
                : dateRangePresets.map((preset) => (
                    <button
                      key={preset.value}
                      type="button"
                      role="option"
                      aria-selected={selectedPreset === preset.value}
                      className={`preset-button ${selectedPreset === preset.value ? 'active' : ''}`}
                      onClick={() => handleRangePresetSelect(preset)}
                    >
                      {preset.label}
                    </button>
                  ))}
            </div>
          </div>

          <div className="date-range-calendar-col">
            <div className="calendar-header">
              <span className="calendar-header-label">
                {selectionMode === 'range' ? 'Custom range' : 'Custom date'}
              </span>
              <span className="calendar-selection-summary">{getSelectionSummary()}</span>
            </div>
            <div className="custom-picker">
              <DatePicker
                selectsRange={selectionMode === 'range'}
                startDate={selectionMode === 'range' ? tempStartDate : null}
                endDate={selectionMode === 'range' ? tempEndDate : null}
                selected={selectionMode === 'single' ? tempStartDate : null}
                onChange={handleDateChange}
                dateFormat="dd/MM/yyyy"
                inline
                showPopperArrow={false}
                calendarClassName="custom-datepicker-calendar"
                calendarStartDay={1}
                minDate={new Date('1900-01-01')}
                maxDate={new Date('2100-12-31')}
                autoComplete="off"
                isClearable={false}
                strictParsing={false}
              />
            </div>
          </div>
        </div>

        <div className="date-range-footer">
          <button
            type="button"
            className="drp-btn drp-btn--ghost"
            onClick={handleClearSelection}
          >
            Clear
          </button>
          <button
            type="button"
            className="drp-btn drp-btn--primary"
            onClick={handleApplyCustomSelection}
            disabled={!canApply}
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div
      className={`date-range-picker-container ${className}`}
      ref={containerRef}
      style={themeStyle}
    >
      <div
        id={id}
        className={`date-range-input ${isOpen ? 'active' : ''} ${disabled ? 'disabled' : ''}`}
        onClick={toggleOpen}
        onKeyDown={handleTriggerKeyDown}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        aria-disabled={disabled}
      >
        <i className="fa fa-calendar-alt me-2" aria-hidden="true"></i>
        <span className="date-range-text">{getDisplayText()}</span>
        <i
          className={`fa fa-chevron-down ms-auto ${isOpen ? 'rotated' : ''}`}
          aria-hidden="true"
        ></i>
      </div>

      {isOpen &&
        !disabled &&
        hasMounted &&
        dropdownPosition &&
        createPortal(dropdownContent, document.body)}
    </div>
  );
};

export default DateRangePicker;
